<script setup lang="ts">
import { useApollonConfFk } from '@/composables/apollonConfFk'
import { type ApollonConfFk } from '@/models/ApollonConfFk'
import { FoerderkategorieGruppe } from '@/models/FoerderkategorieGruppe'
import { createModel } from '@kp/js-model'
import { onMounted, ref, watch, type Ref } from 'vue'

/**
 * Loads ApollonConfFk records that defines the valid
 * Foerderkategorie/Kantone to create new Gesuche from.
 *
 * Creates a selectable list with FK-Groups and FKs,
 * filtered by a given Kanton, if needed.
 *
 * Emits the selected ApollonConfFk model. Note that this is
 * not the FULL model, but only a partial, virtual representation
 * with ID and a display name.
 */

const modelValue = defineModel<ApollonConfFk[] | null>()

const { kantonKuerzel } = defineProps<{
    kantonKuerzel: string | null
}>()

const gruppen: Ref<{ [gruppe_id: number]: FoerderkategorieGruppe }> = ref({}) // map of id => FoerderkategorieGruppe
const apollonFkConfigList: Ref<{ [key: number]: ApollonConfFk[] }> = ref({}) // map of fk-gruppe-id => [ApollonConfFk]
const loading = ref(false)
const records: Ref<ApollonConfFk[]> = ref([])

onMounted(async () => {
    loading.value = true
    records.value = await useApollonConfFk()
    buildGruppeTree(kantonKuerzel)
    loading.value = false
})

watch(
    () => kantonKuerzel,
    (kantonKuerzel: string | null) => {
        buildGruppeTree(kantonKuerzel)
    },
)

function buildGruppeTree(kantonKuerzel: string | null) {
    kantonKuerzel = (kantonKuerzel || '').toLowerCase()
    const tmpGruppen: { [gruppe_id: number]: FoerderkategorieGruppe } = {}
    const tmpApollonFkConfigList: { [key: number]: ApollonConfFk[] } = {}

    // Build FK-Gruppe and FK maps for the given Kanton:
    if (records.value.length > 0) {
        records.value.forEach((conf: ApollonConfFk) => {
            if (conf.kanton_kuerzel?.toLowerCase() !== kantonKuerzel) {
                return
            }
            // Groups:
            const gruppe = createModel(FoerderkategorieGruppe, {
                id: conf.fk_gruppe_id,
                bezeichnung: conf.foerderkategorie_gruppe,
                hilfetext: conf.foerderkategorie_gruppe_hilfetext,
            })
            const gruppe_id = conf.fk_gruppe_id || -1
            tmpGruppen[gruppe_id] = gruppe

            if (!tmpApollonFkConfigList[gruppe_id]) {
                tmpApollonFkConfigList[gruppe_id] = []
            }

            tmpApollonFkConfigList[gruppe_id].push(conf)
        })
    }
    gruppen.value = tmpGruppen
    apollonFkConfigList.value = tmpApollonFkConfigList
}
</script>

<template>
    <div style="max-width: 950px">
        <v-expansion-panels>
            <v-expansion-panel v-for="gruppe in gruppen" :key="`d-${gruppe.id}`">
                <v-expansion-panel-title>
                    {{ gruppe.bezeichnung }}
                </v-expansion-panel-title>
                <v-expansion-panel-text>
                    <v-list v-model:selected="modelValue" select-strategy="single-leaf" three-line>
                        <v-list-subheader v-if="gruppe.hilfetext" :key="`sh-${gruppe.id}`">
                            {{ gruppe.hilfetext }}
                        </v-list-subheader>
                        <v-divider :key="`d-${gruppe.id}`" />
                        <v-list-item
                            v-for="kat in apollonFkConfigList[gruppe.id || -1]"
                            :key="kat.id || 'new'"
                            :value="kat"
                        >
                            <v-list-item-title>{{ kat.bezeichnung_apollon }}</v-list-item-title>
                            <div v-if="kat.hilfetext" style="color: #aaa">
                                {{ kat.config }}
                                <span v-html="kat.hilfetext"></span>
                            </div>
                        </v-list-item>
                        <v-overlay absolute :value="loading">
                            <v-progress-circular indeterminate />
                        </v-overlay>
                    </v-list>
                </v-expansion-panel-text>
            </v-expansion-panel>
        </v-expansion-panels>
    </div>
</template>
